import React, { PropsWithChildren } from 'react';

import { TextField } from 'Containers/App/types';
import { Spinner } from 'DesignLibrary/atoms/Spinner';
import { InfoBarStyle } from 'DesignLibrary/molecules/InfoBar';
import { PrimaryColors } from 'DesignLibrary/vars';
import RichText from 'Shared/RichText';
import Text from 'Shared/Text';

import { InfoBarLoaderContainer, SpinnerContainer, SpinnerText, InfoBarOuterContainer } from './styled';

export interface InfoBarLoaderProps {
  backgroundColor?: PrimaryColors;
  spinnerColor?: string;
  isLoading: boolean;
  isRichText?: boolean;
  field: TextField;
}

/**
 * This component shows a loading spinner in the same style as the InfoBar that will eventually load.
 * It uses the intrinsic size of the InfoBar that will eventually load to avoid layout shifts.
 *
 * @param props.isLoading {boolean} - Boolean value to indicate whether the spinner should display or not
 * @param props.field {TextField} - TextField to display Contenful text in the info bar
 * @param props.isRichText {boolean} - Whether to use RichText or Text component for error text. This should match the Contentful field type,
 * where it is RichText, or a text field.
 * @param props.children {React.ReactNode} - This is the React node that will display when loading has finished
 */
export const InfoBarLoader = ({
  backgroundColor = 'blue',
  spinnerColor = '--primary-blue',
  isLoading,
  field,
  isRichText = false,
  children,
}: PropsWithChildren<InfoBarLoaderProps>) => {
  return (
    <InfoBarOuterContainer>
      {isLoading && (
        <InfoBarLoaderContainer>
          <InfoBarStyle color={backgroundColor} colorText stretch showAccent>
            <SpinnerContainer>
              <Spinner size="large" color={spinnerColor} />
            </SpinnerContainer>
            <SpinnerText>{isRichText ? <RichText field={field} /> : <Text field={field} />}</SpinnerText>
          </InfoBarStyle>
        </InfoBarLoaderContainer>
      )}
      {/* Hide the children, but include in layout flow, to avoid layout shift */}
      <div className={!isLoading ? '' : 'hidden'}>{children}</div>
    </InfoBarOuterContainer>
  );
};
